import IUserPreferencesForControlBar, {
  IUserPreferencesForControlBarV2
} from "../models/view/lessonPage/IUserPreferencesForControlBar";
import {localStorageService} from "./storage/local-storage";
import storageKeys from "./storage/storageKeys";
import {NotationLayoutEnum} from "../models/view/lessonPage/NotationLayoutEnum";
import {logger} from "./log/logger";

/**
 * Retrieves the user preferences for the lesson player.
 *
 * This function ensures that all user preferences are populated with valid values.
 * It first attempts to retrieve the preferences from local storage. If any property
 * is missing, it uses the default preferences as a fallback. If any changes are made
 * to the preferences (e.g., missing properties are filled), the updated preferences
 * are saved back to local storage.
 *
 * @returns {IUserPreferencesForControlBarV2}
 */
export const getUserPreferencesForControlBar = (): IUserPreferencesForControlBarV2 => {
  const userPreferencesForControlBarFromLocalStorage
    = localStorageService.getJsonItem<IUserPreferencesForControlBar | IUserPreferencesForControlBarV2>(storageKeys.segmentPlayer_userPreferencesForControlBar);
  const defaultPreferences = getDefaultUserPreferencesForControlBar();

  const finalUserPreferencesForControlBar = getDefaultUserPreferencesForControlBar();
  let didSomethingChange = false;

  // Go through each property in userPreferencesForControlBar and make sure each property has a value.
  // If a property is missing, set it to the default value from defaultPreferences.
  for (const key in defaultPreferences) {
    // Try to pull the value from localStorage first. If that doesn't work, use the default from defaultPreferences.
    if (userPreferencesForControlBarFromLocalStorage && (key in userPreferencesForControlBarFromLocalStorage)) {
      // @ts-ignore
      finalUserPreferencesForControlBar[key] = userPreferencesForControlBarFromLocalStorage[key];
    } else {
      // @ts-ignore
      finalUserPreferencesForControlBar[key] = defaultPreferences[key];

      // userPreferencesForControlBarFromLocalStorage never has these properties, so we don't need to check changes to them.
      if (key !== "isBirdsEyeKeyboardEnabledForSegmentsWithVideoOnly" && key !== "isBirdsEyeKeyboardEnabledForSegmentsWithBothVideoAndNotation") {
        didSomethingChange = true;
      }
    }
  }

  if (didSomethingChange) {
    setUserPreferencesForControlBar(finalUserPreferencesForControlBar);
  }

  return finalUserPreferencesForControlBar;
};

export const setUserPreferencesForControlBar = (userPreferencesForControlBar: IUserPreferencesForControlBar): void => {
  localStorageService.setJsonItem<IUserPreferencesForControlBar>(storageKeys.segmentPlayer_userPreferencesForControlBar, userPreferencesForControlBar);
};

/**
 * Single source of truth for "merge a partial update into the persisted control-bar
 * preferences". Reads current prefs, merges the partial on top, debug-logs the
 * result, and writes it back. Lives here (the leaf service) so callers can use it
 * without dragging in higher-level services and without each layer having to
 * re-implement the same get/merge/log/persist sequence.
 */
export const partiallySetUserPreferencesForControlBar = (partial: Partial<IUserPreferencesForControlBar>): void => {
  const userPreferencesForControlBar = getUserPreferencesForControlBar();
  const newPrefs = {...userPreferencesForControlBar, ...partial};
  logger.lessonSettings.debug({message: `new prefs for control bar: ` + JSON.stringify(newPrefs)});
  setUserPreferencesForControlBar(newPrefs);
};

const getDefaultUserPreferencesForControlBar = (): IUserPreferencesForControlBarV2 => {
  return {
    appliedNotationLayout: NotationLayoutEnum.vertical, // This can be anything. It will be adjusted when play is pressed if it isn't set correctly based on the selectedNotationLayout.
    areRhythmCountsEnabled: false,
    demoMode: "both hands",
    hidePlayheadDuringPlayback: false,
    isBackingTrackEnabled: true,
    isBirdsEyeKeyboardEnabled: true,
    isBirdsEyeKeyboardEnabledForSegmentsWithBothVideoAndNotation: true,
    isBirdsEyeKeyboardEnabledForSegmentsWithVideoOnly: false,
    isCurrentMeasureHighlighted: false,
    isDemoLeftHandSoundEnabled: false,
    isDemoRightHandSoundEnabled: false,
    isFingeringEnabled: true,
    isInstantFeedbackEnabled: true,
    isNotationLayoutEnabled: true,
    isNoteNamesEnabled: false,
    isScrollDirectionEnabled: true,
    selectedNotationLayout: NotationLayoutEnum.auto,
    pageCount: 0,
    speedPercent: 1,
    zoomAmount: 0,
  };
};
